<script lang="ts" setup>
import { useAtlas, computed, useRouter, useAsyncData, onMounted, useTemplateRef, ref } from '#imports';
import type { Filters } from '#imports';
const {
  filteredSpots,
  filterSpots,
  spotCategoryFilter,
  spotDestinationFilter,
  spotCountriesFilter,
  spotCategories,
  spotCountries,
  spotDestinations,
  spotSearch,
  fetchSpots,
} = useAtlas();

const router = useRouter()
import { useBreakpoints, watchDebounced } from '@vueuse/core';
const breakpoints = useBreakpoints({ mobile: 920, });
const isMobile = breakpoints.smaller('mobile');
const { refresh }= await useAsyncData(async () => await fetchSpots());
watchDebounced(
  () => [
    ...spotCategoryFilter.value,
    ...spotDestinationFilter.value,
    ...spotCountriesFilter.value,
    ...spotSearch.value
  ],
  async () => {
    const filters = {
      categories: {
        name: 'category',
        value: spotCategoryFilter.value,
        referencedTable: 'spots',
        referencedColumn: 'category_id',
      },
      destinations: {
        name: 'destination',
        value: spotDestinationFilter.value,
        referencedTable: 'spots',
        referencedColumn: 'cities_id',
      },
      countries: {
        name: 'countries',
        value: spotCountriesFilter.value,
        referencedTable: 'spots.country_name', // need !inner to handle this 3rd level join
        referencedColumn: 'country',
      },
    };
    await fetchSpots(filters, spotSearch.value);
  },
  {
    immediate: true,
    debounce: 250,
    maxWait: 1000
  }
);

const showFilters = ref(false);
const toggleFilters = () => {
  showFilters.value = !showFilters.value;
}

const hasFiltersApplied = computed(() => {
  return (
    spotDestinationFilter.value.length > 0 ||
    spotCategoryFilter.value.length > 0 ||
    spotCountriesFilter.value.length > 0
  );
});

const clearFilters = async () => {
  spotDestinationFilter.value = [];
  spotCategoryFilter.value = [];
  spotCountriesFilter.value = [];
  spotSearch.value = '';
  await refresh();
};

const spotRemoved = () => {
  setTimeout(async () => {
    await refresh()
  }, 1000);
};
</script>

<template>
  <!-- <h1 :class="[`view-style--${viewStyle}`]">Spots</h1> -->
  <Teleport to="#page-filters">
    <div class="filters-container">
      <MPInput
        label="search"
        v-model="spotSearch"
        placeholder="Search your saved Spots"
        hide-label
        rounded
      />
      <!-- <MPSelect placeholder="Region" hide-label/> -->
      <!-- <MPButton label="Discover Spots" version="secondary" @click="router.push('/explore')"/> -->
      <MPButton
        @click="toggleFilters"
        version="secondary"
        hide-label
        icon="ri-equalizer-line"
        icon-only
        :notification="hasFiltersApplied"
        rounded
      />
      <div v-if="showFilters" class="filters">
        <MPSelect
          hide-label
          label="Countries"
          placeholder="Select a Country"
          v-model="spotCountriesFilter"
          :options="spotCountries"
          searchable
          multiple
        />
        <MPSelect
          hide-label
          label="Destinations"
          placeholder="Select a Destination"
          v-model="spotDestinationFilter"
          :options="spotDestinations"
          searchable
          multiple
        />
        <MPSelect
          hide-label
          label="Categories"
          placeholder="Select a Category"
          v-model="spotCategoryFilter"
          :options="spotCategories"
          searchable
          multiple
        />
        <MPButton label="Done" version="secondary" @click="toggleFilters"/>
        <MPButton v-if="hasFiltersApplied" label="Clear Filters" version="secondary" @click="clearFilters"/>
      </div>
    </div>
  </Teleport>
  <div v-if="filteredSpots.length > 0" class="spots">
    <TransitionGroup name="list">
      <div v-for="bookmark in filteredSpots" :key="bookmark.id">
        <MPSpotCard
          :to="`/atlas/spots/${bookmark.current_slug}`"
          version="small"
          show-tier
          :spot="bookmark"
          show-destination
          actions
          @updated="spotRemoved"
          :creator="bookmark?.maps?.profiles"
        />
      </div>
    </TransitionGroup>

    <div class="actions">
      <MPButton label="Explore more Maps" @click="router.push('/explore')"/>
      <MPButton v-if="hasFiltersApplied" label="Clear Filters" version="secondary" @click="clearFilters"/>
    </div>
  </div>
  <div v-else-if="filteredSpots && filteredSpots.length === 0">
    <MPEmptyState
      v-if="hasFiltersApplied || spotSearch"
      title="No Results"
      videoSrc="/media/videos/empty-state-maps.webm"
      subtitle="We could not find any Spots that match your filters"
    >
      <MPButton label="Clear Filters" version="info" @click="clearFilters"/>
    </MPEmptyState>
    <MPEmptyState
      v-else-if="!hasFiltersApplied || !spotSearch"
      title="No saved Spots"
      videoSrc="/media/videos/empty-state-maps.webm"
      subtitle="You've not got any saved Spots, explore our creature curated Spots and save them to your atlas"
    >
      <MPButton label="Explore" @click="router.push('/explore')"/>
    </MPEmptyState>
  </div>
</template>

<style lang="postcss" scoped>
@import 'assets/css/breakpoints.pcss';
@import 'assets/css/typography.pcss';
.spots {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.75rem;
  justify-content: space-between;
  padding: 0.5rem 1rem 1.5rem;
  @mixin screen md {
    padding: 4rem 1.5rem 1.5rem;
    /* padding-top: v-bind(showFilters ? '0' : '3.5rem'); */
    /* transition: padding 0.1s ease-in; */
  }
}

.filters-container {
  display: grid;
  grid-template-columns: 1fr 2.75rem;
  gap: 0.75rem;
  min-width: calc(100% - 5.5rem);
  @mixin screen md {
    max-width: 100%;
    position: relative;
    padding: 0.5rem 1rem;
  }
}

.filters {
  align-items: center;
  max-width: fit-container;
  background-color: white;
  z-index: 100;
  position: fixed;
  display: grid;
  gap: 1rem;
  top: 50%;
  right: 50%;
  transform: translateY(-50%) translateX(50%);
  padding: 1rem;
  width: 80vw;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--color-border);
  @mixin screen md {
    margin-top: 2rem;
    position: absolute;
    max-width: 20rem;
    transform: none;
    right: -21.5rem;
    top: -1.5rem;
  }
}

.actions {
  display: grid;
  gap: 0.5rem;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.25s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
